import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence, LazyMotion, m } from "framer-motion";
import { useState } from "react";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import * as yup from "yup";
import VaultLogo from "../../../public/svg/vault-logo-with-name-horizontal-mono.svg";
import useAnalytics from "../../hooks/analytics";
import useJSONP from "../../hooks/jsonp";
import { useSnackbar } from "../../hooks/snackbar";
import {
  GradientText1,
  GradientText2,
} from "../../layouts/landing/titleContent/shared";
import {
  APP_STORE,
  INSTAGRAM_HANDLE,
  MAILCHIMP_SIGNUP_ADDRESS,
  PLAY_STORE,
  THREADS_HANDLE,
  TIKTOK_HANDLE,
  TWITTER_HANDLE,
} from "../../lib/constants";
import {
  largeScreenAndAbove,
  largeScreenAndBelow,
  mediumScreenAndBelow,
  smallScreenAndAbove,
  xSmallScreen,
} from "../../lib/mediaQuery";
import { Flex } from "../Grid";
import { Icon, IconNames } from "../Icon";
import { Body, Caption1, Footnote, Headline, Title2 } from "../Typography";
import { PulsingIcon } from "../animation/PulsingIcon";
import { Button } from "../buttons/Button";
import { Input } from "../inputs/Input";

type MailchimpResponse = {
  msg: string;
  result: "success" | "error";
};

interface Props {
  className?: string;
}
export const Footer = ({ className }: Props) => (
  <OuterContainer className={className}>
    <InnerContainer>
      <LinkContainer>
        <FooterLinks
          title={"Support"}
          links={{
            "Email Support": "mailto:support@vault.fan",
          }}
        />
        <FooterLinks
          title={"Legal"}
          css={`
            ${xSmallScreen(css`
              display: none;
            `)}
          `}
          links={{
            "Terms of Use": "/terms-of-service",
            "Privacy Policy": "/privacy-policy",
            "Copyright Notice": "/copyright-notice",
          }}
        />
        <FooterLinks
          title={"Get our App"}
          links={{
            "Download on iOS": APP_STORE,
            "Download on Android": PLAY_STORE,
          }}
          css={`
            ${smallScreenAndAbove(css`
              display: none;
            `)}
          `}
        />
      </LinkContainer>
      <LinkContainer
        css={`
          ${xSmallScreen(css`
            gap: var(--spacing-xs);
          `)}
        `}
      >
        <FooterSocialLinks
          title={"Follow Us"}
          links={{
            Twitter: {
              url: `https://twitter.com/${TWITTER_HANDLE}`,
              icon: "Twitter",
            },
            Instagram: {
              url: `https://instagram.com/${INSTAGRAM_HANDLE}`,
              icon: "Instagram",
            },
            TikTok: {
              url: `https://tiktok.com/@${TIKTOK_HANDLE}`,
              icon: "TikTok",
            },
            Threads: {
              url: `https://threads.net/@${THREADS_HANDLE}`,
              icon: "Threads",
            },
            // Discord: {
            //   url: `https://discord.com/${DISCORD_SERVER}`,
            //   icon: "Discord",
            // },
          }}
        />
        <FooterLinks
          title={"Get our App"}
          links={{
            "Download on iOS": APP_STORE,
            "Download on Android": PLAY_STORE,
          }}
          css={`
            ${xSmallScreen(css`
              display: none;
            `)}
          `}
        />
        <TitleText
          css={`
            ${largeScreenAndAbove(
              css`
                display: none;
              `
            )}
            ${mediumScreenAndBelow(css`
              padding-top: var(--spacing-sm);
            `)};
            ${xSmallScreen(css`
              padding-top: 0;
              padding-bottom: var(--spacing-sm);
              border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            `)}
          `}
        >
          &copy; Vault Laboratories, Inc.
        </TitleText>
      </LinkContainer>
      <SubscribeContainer>
        <SubscribeForm />
      </SubscribeContainer>
      <LinkContainer
        gap={"md"}
        css={mediumScreenAndBelow(
          css`
            display: none;
          `
        )}
      >
        <VaultLogo />
        <TitleText
          css={`
            padding-bottom: 0;
          `}
        >
          &copy; Vault Laboratories, Inc.
        </TitleText>
      </LinkContainer>
    </InnerContainer>
  </OuterContainer>
);

const LinkContainer = styled(Flex).attrs({ direction: "column" })`
  width: 100%;
  max-width: 250px;
  ${xSmallScreen(css`
    max-width: 200px;
  `)}
`;
const SubscribeContainer = styled(LinkContainer)`
  min-height: 200px;
  justify-content: center;
  max-width: 347px;
  gap: 8px;
  ${largeScreenAndBelow(
    css`
      display: none;
    `
  )}
`;

interface FooterSocialLinkProps {
  title: string;
  links: { [name: string]: { url: string; icon: IconNames } };
}

const FooterSocialLinks = ({ title, links }: FooterSocialLinkProps) => (
  <FooterLinksContent
    direction={"column"}
    justify={"flex-end"}
    align={"center"}
    paddingBottom={"sm"}
    paddingTop={"sm"}
    gap={"xs"}
    css={`
      & > * {
        width: 100%;
      }
    `}
  >
    <TitleText>{title}</TitleText>
    <Flex direction={"row"} gap={"sm"}>
      {Object.entries(links).map(([name, props], i) => (
        <a href={props.url} key={`footer_social_links_${i}`} target={"_blank"}>
          <Icon name={props.icon} title={name} height={20} width={20} />
        </a>
      ))}
    </Flex>
  </FooterLinksContent>
);

interface FooterLinkProps {
  title: string;
  links: { [name: string]: string };
  className?: string;
}

const FooterLinks = ({ title, links, className }: FooterLinkProps) => (
  <FooterLinksContent
    direction={"column"}
    justify={"flex-end"}
    align={"center"}
    paddingBottom={"sm"}
    paddingTop={"sm"}
    gap={"xs"}
    className={className}
    css={`
      & > * {
        width: 100%;
      }
    `}
  >
    <TitleText>{title}</TitleText>
    {Object.entries(links).map(([name, url], i) => (
      <BodyText key={`footer_links_${i}`}>
        <a href={url}>{name}</a>
      </BodyText>
    ))}
  </FooterLinksContent>
);

const FooterLinksContent = styled(Flex)`
  max-width: 250px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  a {
    text-decoration: none;
    color: var(--white-light);
  }
`;

const InnerContainer = styled(Flex).attrs({
  direction: "row",
  justify: "space-between",
  align: "flex-end",
  gap: "lg",
})`
  width: 100%;
  max-width: var(--page-max-width);
  display: flex;
  ${largeScreenAndBelow(css`
    justify-content: space-around;
  `)}
  ${xSmallScreen(css`
    justify-content: space-between;
  `)}
`;

const OuterContainer = styled.footer`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: var(--footer-height);
  padding: var(--spacing-md);
  left: 0;
  right: 0;
  bottom: 0;

  background: rgba(255, 255, 255, 0.05);
`;

const TitleText = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <>
    <Headline
      className={className}
      css={xSmallScreen(
        css`
          display: none;
        `
      )}
    >
      {children}
    </Headline>
    <Footnote
      className={className}
      css={smallScreenAndAbove(
        css`
          display: none;
        `
      )}
    >
      {children}
    </Footnote>
  </>
);

const BodyText = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <>
    <Body
      className={className}
      css={xSmallScreen(
        css`
          display: none;
        `
      )}
    >
      {children}
    </Body>
    <Caption1
      className={className}
      css={smallScreenAndAbove(
        css`
          display: none;
        `
      )}
    >
      {children}
    </Caption1>
  </>
);

const schema = yup.object({
  email: yup.string().email("Email must be valid").required("Email required"),
});

interface NewsletterFormData {
  email: string;
}

const SubscribeForm = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { trackEvent } = useAnalytics();
  const pushSnack = useSnackbar();
  const { handleSubmit, formState, register } = useForm({
    resolver: yupResolver(schema),
  });
  const submitSubscribe = useJSONP<MailchimpResponse>(
    MAILCHIMP_SIGNUP_ADDRESS,
    {
      callbackParam: "c",
    }
  );

  const subscribeToNewsletter = (data: NewsletterFormData) =>
    submitSubscribe({ EMAIL: data.email }).then((data) => {
      if (data.result === "success") {
        return data;
      }
      throw new Error(data.msg);
    });

  const mutation = useMutation(subscribeToNewsletter, {
    onMutate() {
      setIsLoading(true);
    },
    onSuccess() {
      trackEvent("marketing_general_sign_up_success");
      setIsSubmitted(true);
      pushSnack({
        key: "subscribe-to-newsletter",
        message: "You're subscribed to our newsletter",
        type: "success",
      });
    },
    onError(error: Error) {
      trackEvent("marketing_general_sign_up_fail");
      console.error(error);
      pushSnack({
        key: "subscribe-to-newsletter-error",
        message: error.message,
        type: "error",
      });
    },
    onSettled() {
      setIsLoading(false);
    },
  });

  const ANIMATION_DURATION_SECONDS: number = 0.5;

  return (
    <>
      <LazyMotion features={loadFeatures}>
        <AnimatePresence>
          {isSubmitted ? (
            <m.div
              key={"submitted"}
              transition={{ duration: ANIMATION_DURATION_SECONDS }}
              initial={{ opacity: 0, position: "absolute" }}
              animate={{ opacity: 1 }}
              css={`
                display: flex;
                align-self: center;
              `}
            >
              <PulsingIcon
                icon={"Checkmark"}
                centerColor={"var(--magenta-diagonal-gradient)"}
                ringColor={"rgb(var(--purple-mid))"}
                ringSize={2}
              />
            </m.div>
          ) : (
            <m.div
              key={"form"}
              transition={{ duration: ANIMATION_DURATION_SECONDS }}
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, pointerEvents: "none" }}
            >
              <form
                noValidate
                onSubmit={handleSubmit((data: NewsletterFormData) =>
                  mutation.mutate(data)
                )}
              >
                <Title2>
                  Be <GradientText1>first</GradientText1> to hear about new
                  drops
                  <GradientText2>.</GradientText2>
                </Title2>
                <Input
                  type="email"
                  placeholder="Enter your email here..."
                  shape={"pill"}
                  variant={"outline"}
                  // @ts-ignore
                  error={formState.errors?.email}
                  {...register("email")}
                  css={`
                    margin-top: var(--spacing-sm);
                  `}
                />
                <Button
                  variant={"primary-alt"}
                  shape={"pill"}
                  loading={isLoading}
                  disabled={isSubmitted}
                  type={"submit"}
                  css={`
                    margin: 0;
                    margin-top: var(--spacing-sm);
                  `}
                >
                  Subscribe
                </Button>
                <Caption1
                  css={`
                    margin-top: var(--spacing-md);
                  `}
                >
                  By subscribing you agree to the{" "}
                  <a href={"/terms-of-service"}>terms of use</a> and{" "}
                  <a href={"/privacy-policy"}>privacy policy</a>.
                </Caption1>
              </form>
            </m.div>
          )}
        </AnimatePresence>
      </LazyMotion>
    </>
  );
};

const loadFeatures = () =>
  import("../../framer-motion.js").then((res) => res.default);

export default Footer;
